<template>
  <div>
    <div>Forms required for the drivers arrival and departure:</div>
    <div class="disclaimer font-size-x-small">
      Customize your forms at the warehouse
      <router-link
        :to="{
          name: 'warehouses.details.forms',
          params: { warehouseId }
        }">
        Fields and Forms
      </router-link>
      section.
    </div>

    <template v-if="isGateManagementEnabled">
      <custom-forms-data-panels
        class="mt-4"
        :editable="false"
        :object-ids="{
          appointmentId: appointment.id,
          assetVisitId: appointment.assetVisit?.id
        }"
        :appointment="appointment"
        :triggers="staticTriggers"
        :warehouse="appointment.warehouse"
        :timezone="appointment.warehouse.timezone"
        :military-time-enabled="$isMilitaryTimeEnabled(appointment.warehouse)">
        <template #additional-disclaimer="{ trigger, lastChangedDateTimesInTimezone }">
          <span
            v-if="!lastChangedDateTimesInTimezone[trigger?.id] && !appointment.assetVisit?.id"
            class="text--color-text-disabled">
            {{ CustomFormsFeaturesData[trigger?.feature]?.title?.split(':')[0] }}
            pending
          </span>
          <span
            class="text--color-text-disabled"
            v-else-if="
              !lastChangedDateTimesInTimezone[trigger?.id] &&
              appointment.assetVisit?.id &&
              trigger?.id !== assetDetailsTrigger?.id
            ">
            No appointment details provided
          </span>
        </template>
        <template
          v-if="appointment.assetVisit?.id"
          v-slot:[`additional-form-data-${assetDetailsTrigger?.id}`]>
          <asset-visit-presenter
            :asset-visit="{
              id: appointment.assetVisit.id,
              phone: appointment.assetVisit.phone
            }" />
        </template>
      </custom-forms-data-panels>

      <v-expansion-panels flat multiple v-if="isYMSEnabled">
        <appointment-details-gate-yard-yard
          :appointment="appointment"
          :triggers="assetDetailsTriggers" />
      </v-expansion-panels>

      <v-expansion-panels flat v-if="isNavtracIntegrationEnabled" :data-testid="'navtrac-details'">
        <appointment-details-gate-yard-navtrac :appointment="appointment" />
      </v-expansion-panels>
    </template>

    <v-expansion-panels flat multiple v-else>
      <appointment-details-gate-yard-disclaimer :appointment="appointment" />
    </v-expansion-panels>

    <custom-forms-data-panels
      v-if="hasActiveTriggers"
      ref="editableFormsPanel"
      :editable="isCheckoutEditable"
      :object-ids="{
        appointmentId: appointment.id,
        assetVisitId: appointment.assetVisit?.id
      }"
      :appointment="appointment"
      :triggers="editableTriggers"
      :warehouse="appointment.warehouse"
      :timezone="appointment.warehouse.timezone"
      :military-time-enabled="$isMilitaryTimeEnabled(appointment.warehouse)">
      <template #additional-disclaimer="{ trigger, lastChangedDateTimesInTimezone }">
        <span v-if="!lastChangedDateTimesInTimezone[trigger?.id]" class="text--color-text-disabled">
          {{ CustomFormsFeaturesData[trigger?.feature]?.title?.split(':')[0] }}
          pending
        </span>
      </template>
    </custom-forms-data-panels>
    <v-alert class="mt-4" border="start" border-color color="warning" v-else>
      No Gate Management forms have been enabled for this warehouse
    </v-alert>
  </div>
</template>
<script>
import { computed, ref, watch } from 'vue';
import AssetVisitPresenter from '@satellite/components/elements/presenters/asset_visit/AssetVisitPresenter.vue';
import {
  WarehouseFeatureEnum,
  isFeatureEnabledForWarehouse,
  CustomFormsFeaturesData,
  CustomFormsFeatures,
  AppointmentStatus
} from '@nova/core';
import CustomFormsDataPanels from '@/modules/custom_forms/components/CustomFormsDataPanels.vue';
import AppointmentDetailsGateYardNavtrac from '@/modules/appointments/components/details/GateYard/AppointmentDetailsGateYardNavtrac.vue';
import AppointmentDetailsGateYardYard from '@/modules/appointments/components/details/GateYard/AppointmentDetailsGateYardYard.vue';
import AppointmentDetailsGateYardDisclaimer from '@/modules/appointments/components/details/GateYard/AppointmentDetailsGateYardDisclaimer.vue';
import { useFlag } from '@loadsmart/vue-feature-flags';

export default {
  components: {
    AssetVisitPresenter,
    CustomFormsDataPanels,
    AppointmentDetailsGateYardNavtrac,
    AppointmentDetailsGateYardYard,
    AppointmentDetailsGateYardDisclaimer
  },
  props: {
    appointment: {
      type: Object,
      required: true
    },
    gateManagementTriggers: {
      type: Array,
      required: true
    },
    assetDetailsTriggers: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  setup(props) {
    const editableTriggerNames = [CustomFormsFeatures.CheckOut];

    const editableFormsPanel = ref(null);
    const isCheckoutEditable = ref(false);
    const isLoading = ref(false);

    const featureFlagNavtracUIEnabled = useFlag('enable-navtrac-details-ui')?.value.enabled;

    const isNavtracIntegrationEnabled = computed(() => {
      return (
        isFeatureEnabledForWarehouse(props.appointment.warehouse, WarehouseFeatureEnum.NavTrac) &&
        featureFlagNavtracUIEnabled
      );
    });

    const isGateManagementEnabled = computed(() => {
      return isFeatureEnabledForWarehouse(
        props.appointment.dock.warehouse,
        WarehouseFeatureEnum.GateManagement
      );
    });

    const isYMSEnabled = computed(() => {
      return isFeatureEnabledForWarehouse(props.appointment.warehouse, WarehouseFeatureEnum.YMS);
    });

    const staticTriggers = computed(() => {
      return (props.gateManagementTriggers || []).filter(
        trigger =>
          !editableTriggerNames.some(editable => trigger.feature?.toLowerCase()?.includes(editable))
      );
    });

    const assetDetailsTrigger = computed(() => {
      return (props.gateManagementTriggers || []).find(trigger => {
        return trigger.feature === CustomFormsFeatures.CheckInAdditionalAssetDetails;
      });
    });

    const editableTriggers = computed(() => {
      return (props.gateManagementTriggers || []).filter(trigger => {
        return !staticTriggers.value.includes(trigger);
      });
    });

    const hasActiveTriggers = computed(() => {
      return (props.gateManagementTriggers || []).filter(trigger => trigger.isActive).length > 0;
    });

    const warehouseId = computed(
      () => props.appointment.dock?.warehouseId ?? props.appointment.warehouse.id
    );

    watch(
      () => editableFormsPanel.value?.triggerDataLastChangedDateTimes,
      newVal => {
        if (
          Object.values(newVal).every(value => value) &&
          props.appointment.status === AppointmentStatus.Completed
        ) {
          isCheckoutEditable.value = true;
        } else {
          isCheckoutEditable.value = false;
        }
      }
    );

    return {
      staticTriggers,
      editableTriggers,
      editableFormsPanel,
      isCheckoutEditable,
      isNavtracIntegrationEnabled,
      hasActiveTriggers,
      isGateManagementEnabled,
      editableTriggerNames,
      assetDetailsTrigger,
      CustomFormsFeaturesData,
      isYMSEnabled,
      warehouseId,
      isLoading
    };
  }
};
</script>

<style lang="scss" scoped>
.field-value:not(:first-child) {
  margin-top: 16px;
}

.blurred {
  filter: blur(2px);
}

:deep(.ad-card) {
  .v-card__title {
    font-size: 16px;
    font-weight: 400;
  }

  .v-card__actions {
    margin-top: 16px !important;
    padding: 16px;
    border-top: 1px solid $color-line-border;
  }
}
</style>
